import { useEffect, useRef } from 'react';
import { observe, type ObserveOptions } from './observe';

/** Set `data-revealed` on `element` once it scrolls into view. Returns the cleanup. */
export function revealOnView(element: Element, options?: ObserveOptions): () => void {
  return observe(
    element,
    () => {
      (element as HTMLElement).dataset['revealed'] = 'true';
    },
    options,
  );
}

/** Attach the reveal behaviour to your own element: `<div ref={useReveal()} data-reveal="blur">`. */
export function useReveal<T extends HTMLElement = HTMLElement>(options?: ObserveOptions) {
  const ref = useRef<T | null>(null);
  const once = options?.once;
  const rootMargin = options?.rootMargin;
  const threshold = options?.threshold;
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const settings: ObserveOptions = {
      ...(once === undefined ? {} : { once }),
      ...(rootMargin === undefined ? {} : { rootMargin }),
      ...(threshold === undefined ? {} : { threshold }),
    };
    return revealOnView(element, settings);
  }, [once, rootMargin, threshold]);
  return ref;
}
